/**
 * Persistence setup: builds the IndexedDB-backed persister + persist options.
 *
 * The persister is mounted ONCE and never remounted. Partitioning by identity
 * (user / org / partner) is achieved by the storage adapter resolving the
 * partition key LIVE per read/write (see `idb-storage.ts` + `partition-key.ts`),
 * so login / logout / org-switch route to the correct blob without tearing down
 * the React tree. Restore-gating (paint-from-cache before queries fetch) applies
 * on initial load; mid-session context switches already `queryClient.clear()`
 * and refetch, so they don't need a re-restore.
 */

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import type { PersistQueryClientProviderProps } from '@tanstack/react-query-persist-client'
import { createIdbAsyncStorage, deletePersistedPartition } from './idb-storage'
import { getPersistencePartitionKey } from './partition-key'
import { shouldPersistQuery } from './persist-policy'

/** Bump when the persisted shape of cached responses changes incompatibly. */
const CACHE_SCHEMA_VERSION = 'v1'

/** Discard any persisted blob older than this at restore time (blob-level TTL). */
const MAX_AGE = 24 * 60 * 60 * 1000 // 24h

export type PersistOptions = PersistQueryClientProviderProps['persistOptions']

let cachedPersistOptions: PersistOptions | null = null

export function buildPersistOptions(): PersistOptions {
  if (cachedPersistOptions) return cachedPersistOptions

  const persister = createAsyncStoragePersister({
    // Live partition key: physical isolation between identities is enforced by
    // the IndexedDB key, resolved per operation.
    storage: createIdbAsyncStorage(getPersistencePartitionKey),
    key: 'raven-admin-query-cache',
    throttleTime: 1000,
  })

  // buster only needs the schema version: cross-identity isolation is already
  // guaranteed by the partitioned storage key, so a stale blob from another
  // identity can never be read back under the current partition.
  cachedPersistOptions = {
    persister,
    maxAge: MAX_AGE,
    buster: CACHE_SCHEMA_VERSION,
    dehydrateOptions: {
      shouldDehydrateQuery: shouldPersistQuery,
    },
  }
  return cachedPersistOptions
}

/** Remove the currently-active partition's persisted blob (call on logout). */
export async function clearPersistedCacheForCurrentPartition(): Promise<void> {
  await deletePersistedPartition(getPersistencePartitionKey())
}
